import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PromptingService } from './prompting.service';
import { InferenceClient } from '@huggingface/inference';
import { environment } from '../../../environments/environment';

export interface Txt2ImgPayload {
  prompt: string;
  steps?: number;
  width?: number;
  height?: number;

  negative_prompt?: string;
  styles?: string[];
  seed?: number;
  subseed?: number;
  subseed_strength?: number;
  seed_resize_from_h?: number;
  seed_resize_from_w?: number;
  sampler_name?: string;
  scheduler?: string;
  batch_size?: number;
  n_iter?: number;

  cfg_scale?: number;
  distilled_cfg_scale?: number;
  restore_faces?: boolean;
  tiling?: boolean;
  do_not_save_samples?: boolean;
  do_not_save_grid?: boolean;
  eta?: number;
  denoising_strength?: number;
  s_min_uncond?: number;
  s_churn?: number;
  s_tmax?: number;
  s_tmin?: number;
  s_noise?: number;
  override_settings?: Record<string, any>;
  override_settings_restore_afterwards?: boolean;
  refiner_checkpoint?: string;
  refiner_switch_at?: number;
  disable_extra_networks?: boolean;
  firstpass_image?: string;
  comments?: Record<string, any>;
  enable_hr?: boolean;
  firstphase_width?: number;
  firstphase_height?: number;
  hr_scale?: number;
  hr_upscaler?: string;
  hr_second_pass_steps?: number;
  hr_resize_x?: number;
  hr_resize_y?: number;
  hr_checkpoint_name?: string;
  hr_additional_modules?: string[];
  hr_sampler_name?: string;
  hr_scheduler?: string;
  hr_prompt?: string;
  hr_negative_prompt?: string;
  hr_cfg?: number;
  hr_distilled_cfg?: number;
  force_task_id?: string;
  sampler_index?: string;
  script_name?: string;
  script_args?: any[];
  send_images?: boolean;
  save_images?: boolean;
  alwayson_scripts?: Record<string, any>;
  infotext?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AiImageService {
  private client = new InferenceClient(environment.aiConfig.hfToken);

  private baseUrl = 'https://ai3.learmo.dev/sdapi/v1';

  constructor(private prompting: PromptingService) {}

  generateImage(payload: Txt2ImgPayload): Observable<string> {
    const finalPayload: Txt2ImgPayload | any = {
      ...payload,
      hr_prompt: payload.prompt,
      checkpoint_name: 'flux1-dev-fp8.safetensors',
      scheduler: 'Simple',
      sampler_name: 'Euler',
      cfg_scale: 1,
      steps: 20,
      batch_size: 1,
      restore_faces: false,
      // "sd_model_name": "flux1-dev-fp8",

      seed_resize_from_w: -1,
      seed_resize_from_h: -1,
      is_stream: false,
      stream: false,
      styles: [],
    };
    return new Observable<string>((observer) => {
      fetch(`${this.baseUrl}/txt2img`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalPayload),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const result = await response.json();
          if (result?.images?.length > 0) {
            const imgBase64 = result.images[0];
            observer.next(`data:image/png;base64,${imgBase64}`);
            observer.complete();
          } else {
            throw new Error('No images returned from AI.');
          }
        })
        .catch((err: Error) => {
          this.prompting
            .error(
              'AI Error',
              'Image generation failed. Please try again.',
              'default',
            )
            .then(() => observer.error(err));
        });
    });
  }

  generateImageHF(payload: Txt2ImgPayload): Observable<string> {
    return new Observable<string>((observer) => {
      this.client
        .textToImage({
          model: 'Qwen/Qwen-Image',
          inputs: payload.prompt,
          provider: 'replicate',
          parameters: {
            num_inference_steps: payload.steps ?? 5,
            width: payload.width ?? 1024,
            height: payload.height ?? 576,
          },
        })
        .then((blob: any) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            observer.next(reader.result as string); // ✅ base64 string
            observer.complete();
          };
          reader.readAsDataURL(blob); // ✅ pass the Blob, not a string
        })
        .catch((err: Error) => {
          observer.error(err);
        });
    });
  }
}
